import { DataTable } from '@ibe/ui';

import { type ImportReport } from '../../modules/reference/api/schemas.js';

/**
 * What the file would do, before it does any of it.
 *
 * Counts first, then the errors. An operator with a broken file wants the
 * broken rows, and an operator with a good file wants to know it is good —
 * putting the counts above a scrolling error table serves both.
 */
export interface ImportPreviewProps {
  readonly report: ImportReport;
}

export function ImportPreview({ report }: ImportPreviewProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-3 gap-4">
        <div>
          <dt className="text-sm text-muted-foreground">To create</dt>
          <dd className="text-2xl font-semibold">{report.willCreate}</dd>
        </div>
        <div>
          <dt className="text-sm text-muted-foreground">To update</dt>
          <dd className="text-2xl font-semibold">{report.willUpdate}</dd>
        </div>
        <div>
          <dt className="text-sm text-muted-foreground">Unchanged</dt>
          <dd className="text-2xl font-semibold">{report.unchanged}</dd>
        </div>
      </dl>

      {report.errors.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-destructive">
            {report.errors.length} rows cannot be imported. Fix them in the file and upload it again
            — nothing is applied while any row is invalid.
          </p>
          <div className="max-h-64 overflow-y-auto">
            <DataTable
              caption="Import errors"
              rowKey={(row) => `${String(row.line)}-${row.field}`}
              rows={report.errors}
              columns={[
                { id: 'line', header: 'Line', cell: (row) => row.line },
                { id: 'field', header: 'Column', cell: (row) => row.field },
                {
                  id: 'value',
                  header: 'Value',
                  // Echoed so the operator can search the file for it. An
                  // error naming only the rule is an error they cannot act on.
                  cell: (row) => <code>{row.value === '' ? '(blank)' : row.value}</code>,
                },
                { id: 'message', header: 'Problem', cell: (row) => row.message },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
}
